import { z } from 'zod';
import Api from '../Api';
import type { GetServicesParams, ServicesResponse } from '../../components/services/types/services-api-schemas';
import { CreateRequirementRequest, CreateRequirementResponse } from '../../components/services/types/requirements';

const billingAddressSchema = z.object({
  streetName: z.string().trim()
    .min(1),
  streetNumber: z.string().trim()
    .min(1),
  postalCode: z.string().trim()
    .min(1),
  city: z.string().trim()
    .min(1),
  country: z.string().trim()
    .min(1),
});

const customerSchema = z.object({
  firstName: z.string().trim()
    .min(1),
  lastName: z.string().trim()
    .min(1),
  email: z.string().trim()
    .email(),
  phone: z.string().trim()
    .min(1),
  company: z.string().trim()
    .optional(),
  billingAddress: billingAddressSchema.optional(),
  vatId: z.string().trim()
    .optional(),
  cocNumber: z.string().trim()
    .optional(),
});

const orderGuidanceBookRequestSchema = z.object({
  startDateTime: z.string().trim()
    .min(1),
  customer: customerSchema,
  description: z.string().trim()
    .min(10),
  attachments: z.array(z.string().trim()).optional(),
});

export type OrderGuidanceBookRequest = z.infer<typeof orderGuidanceBookRequestSchema>;

type BookingTimeSlot = {
  startDateTime: string;
  endDateTime: string;
};

type BookingDay = {
  date: string;
  slots: BookingTimeSlot[];
};

type BookingAvailabilityResponse = {
  days: BookingDay[];
};

class Services extends Api {
  async bookOrderGuidance(requestBody: OrderGuidanceBookRequest): Promise<void> {
    const validatedBody = orderGuidanceBookRequestSchema.parse(requestBody);
    return this.httpClient.post(
      '/services/order-guidance/book',
      validatedBody,
      { headers: { 'store-key': process.env.GATSBY_STORE } },
    );
  }

  async getBookingAvailability(): Promise<BookingAvailabilityResponse> {
    const { data } = await this.httpClient.get<BookingAvailabilityResponse>('/services/order-guidance/availability');

    return data;
  }

  async getServicesForProduct({
    productKey,
    facts,
    productPageId,
    locale,
  }: GetServicesParams): Promise<ServicesResponse> {
    const queryParams = new URLSearchParams();

    if (facts && Object.keys(facts).length > 0) {
      queryParams.append('facts', JSON.stringify(facts));
    }

    if (productPageId) {
      queryParams.append('productPageId', productPageId);
    }

    queryParams.append('locale', locale);

    const { data } = await this.httpClient.get<ServicesResponse>(
      `/services/${encodeURIComponent(productKey)}?${queryParams.toString()}`,
      { headers: { 'store-key': process.env.GATSBY_STORE } },
    );

    return data;
  }

  // TODO SERVICES-405: move locale into the body of the request
  async createRequirements(requirements: CreateRequirementRequest, locale: string): Promise<CreateRequirementResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('locale', locale);

    const { data } = await this.httpClient.post<CreateRequirementResponse>(
      `/wishform/requirements?${queryParams.toString()}`,
      requirements,
      { headers: { 'store-key': process.env.GATSBY_STORE } },
    );

    return data;
  }
}

export default Services;
